import { useCallback, useMemo } from 'react';
import {
  SiteConfigurationTemplate,
  TagMapping,
} from '../components/site-configuration-template/types';

/** Build a lookup from source tag id --> display label */
const buildTagNameLookup = (template: SiteConfigurationTemplate): Record<string, string> => {
  const lookup: Record<string, string> = {};
  for (const gw of template.gateways) {
    for (const [rawInputName, tag] of Object.entries(gw.tags ?? {})) {
      lookup[tag.idempotencyKey] = tag.displayName || rawInputName;
    }
  }
  return lookup;
};

/**
 * Shared tag-mapping status helpers used across the Overview sections: a
 * source-tag display-name lookup, and predicates for whether a tag dependency
 * blocks a resource from being included in the import.
 */
export const useTagMappingStatus = (
  template: SiteConfigurationTemplate,
  tagMapping: TagMapping
) => {
  const tagNameLookup = useMemo(() => buildTagNameLookup(template), [template]);

  // A tag dependency is "blocking" only when it IS in the template (can be mapped by the
  // user) but has NOT been mapped. Dependencies on tags outside the template
  // forwarded inputs excluded from the export don't block inclusion.
  const isBlockingUnmapped = useCallback(
    (tagId: string | null | undefined) => {
      if (!tagId) return false;
      const isInTemplate = Object.hasOwn(tagMapping, tagId);
      return isInTemplate && !tagMapping[tagId];
    },
    [tagMapping]
  );

  // Names of tags that are in the template but unmapped (the only ones worth showing to the user).
  const unmappedNames = useCallback(
    (tagIds: string[]) =>
      tagIds.filter((id) => isBlockingUnmapped(id)).map((id) => tagNameLookup[id] || id),
    [isBlockingUnmapped, tagNameLookup]
  );

  return { tagNameLookup, isBlockingUnmapped, unmappedNames };
};
